import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase"; // Import Firestore configuration
import Sidebar from "../components/sidebar";
import Tabs from "../components/tabs";

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    teacherId, 
    teacherData, 
    schoolId, 
    deviceId, 
    sessionId, 
    schoolName, 
    deviceName,
    classIds,
    authMethod 
  } = location.state || {}; // Retrieve session data from state
  
  const [currStudent, setCurrStudent] = useState(null); // Currently selected student
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [teacherName, setTeacherName] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [teacherClasses, setTeacherClasses] = useState([]);

  // Redirect to device login if no session data
  useEffect(() => {
    if (!teacherId || !sessionId) {
      navigate("/");
      return;
    }
  }, [teacherId, sessionId, navigate]);

  // Set teacher name and fetch classes
  useEffect(() => {
    if (teacherData) {
      setTeacherName(teacherData.name || "Teacher");
      
      // Fetch teacher's classes
      const fetchTeacherClasses = async () => {
        if (classIds && classIds.length > 0) {
          try {
            const classesPromises = classIds.map(async (classId) => {
              const classDoc = await getDoc(doc(db, "classes", classId));
              return classDoc.exists() ? { id: classDoc.id, ...classDoc.data() } : null;
            });
            
            const classes = await Promise.all(classesPromises);
            setTeacherClasses(classes.filter(cls => cls !== null));
          } catch (error) {
            console.error("Error fetching teacher's classes:", error);
            setTeacherClasses([]);
          }
        }
      };

      fetchTeacherClasses();
    }
  }, [teacherData, classIds]);

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    }, 1000);

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, []);

  // Function to trigger student list refresh
  const refreshStudents = () => {
    setRefreshTrigger((prev) => prev + 1); // Increment the trigger state
  };

  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  return (
    <div 
      className="home-container min-h-screen"
      style={{ backgroundColor: safeLineColors.lightGray }}
    >
      {teacherId && sessionId ? (
        <>
          
          {/* Header Bar - Material Design Style */}
          <div 
            className="px-6 py-4 shadow-sm border-b"
            style={{ 
              backgroundColor: safeLineColors.pureWhite,
              borderBottomColor: 'rgba(0,0,0,0.1)',
              backgroundImage: 'linear-gradient(to right, #f8f9fa, #ffffff)'
            }}
          >
            <div className="flex justify-between items-center">
              <div>
                <h1 
                  className="text-2xl font-medium mb-1"
                  style={{ 
                    color: '#1a2c4e',
                    fontWeight: 'medium'
                  }}
                >
                  Teacher Dashboard
                </h1>
                <p 
                  className="text-sm"
                  style={{ color: '#6b7280' }}
                >
                  Welcome, {teacherName} • {schoolName}
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* User Info Badge */}
                <div 
                  className="flex items-center px-4 py-2 rounded-full border"
                  style={{ 
                    backgroundColor: safeLineColors.orangeTint,
                    borderColor: safeLineColors.coolGray,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold"
                    style={{ 
                      backgroundColor: safeLineColors.flameOrange,
                      color: safeLineColors.pureWhite
                    }}
                  >
                    {teacherName?.charAt(0)?.toUpperCase() || 'T'}
                  </div>
                  <div>
                    <p 
                      className="text-sm font-medium"
                      style={{ color: safeLineColors.darkCharcoal }}
                    >
                      {teacherName}
                    </p>
                    <p 
                      className="text-xs"
                      style={{ color: safeLineColors.mediumCharcoal }}
                    >
                      {authMethod === "pin" ? "PIN Auth" : "Email Auth"}
                    </p>
                  </div>
                </div>

                {/* Time and Date */}
                <div className="text-right">
                  <div 
                    className="text-lg font-semibold"
                    style={{ color: safeLineColors.darkCharcoal }}
                  >
                    {currentTime}
                  </div>
                  <div 
                    className="text-xs"
                    style={{ color: safeLineColors.mediumCharcoal }}
                  >
                    {new Date().toLocaleDateString('en-US', { 
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </div>

                {/* Logout Button */}
                <button
                  className="flex items-center px-4 py-2 rounded-lg transition-all duration-200 border"
                  style={{
                    backgroundColor: safeLineColors.red,
                    borderColor: safeLineColors.red,
                    color: safeLineColors.pureWhite
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = safeLineColors.darkRed;
                    e.target.style.borderColor = safeLineColors.darkRed;
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = safeLineColors.red;
                    e.target.style.borderColor = safeLineColors.red;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to logout?')) {
                      // Navigate back to device login
                      window.location.href = '/';
                    }
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                  </svg>
                  Logout
                </button>
              </div>
            </div>

            {/* Secondary info bar */}
            <div className="mt-3 pt-3 border-t" style={{ borderTopColor: safeLineColors.coolGray }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: safeLineColors.lightEmerald,
                      color: safeLineColors.forestGreen
                    }}
                  >
                    Active Session
                  </div>
                  <div 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: safeLineColors.lightAmber,
                      color: safeLineColors.darkAmber
                    }}
                  >
                    Device: {deviceName}
                  </div>
                  {teacherClasses.length > 0 && (
                    <div 
                      className="px-3 py-1 rounded-full text-xs font-medium"
                      style={{ 
                        backgroundColor: safeLineColors.orangeTint,
                        color: safeLineColors.flameOrange
                      }}
                    >
                      {teacherClasses.length} Class{teacherClasses.length !== 1 ? 'es' : ''}
                    </div>
                  )}
                </div>
                
                <div className="text-xs" style={{ color: safeLineColors.mutedGray }}>
                  Session active • Auto-logout in 4 hours
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex flex-row h-screen">
            {/* Sidebar */}
            <div 
              className="w-[30%] h-full overflow-y-auto border-r-2 shadow-sm"
              style={{ 
                backgroundColor: safeLineColors.pureWhite,
                borderRightColor: safeLineColors.coolGray
              }}
            >
              <Sidebar
                onSelectStudent={setCurrStudent}
                refreshTrigger={refreshTrigger}
                teacherId={teacherId}
                schoolId={schoolId}
                classIds={classIds}
              />
            </div>

            {/* Main Content */}
            <div 
              className="w-[70%] h-full flex items-center justify-center pt-2 pb-1"
              style={{ backgroundColor: safeLineColors.lightGray }}
            >
              <Tabs
                selectedStudent={currStudent}
                onStatusChange={refreshStudents}
                teacherId={teacherId}
                teacherData={teacherData}
                schoolId={schoolId}
                deviceId={deviceId}
                sessionId={sessionId}
              />
            </div>
          </div>
        </>
      ) : (
        <div 
          className="flex items-center justify-center h-screen"
          style={{ backgroundColor: safeLineColors.lightGray }}
        >
          <div 
            className="text-center p-8 rounded-xl shadow-lg"
            style={{ backgroundColor: safeLineColors.pureWhite }}
          >
            <div 
              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: safeLineColors.orangeTint }}
            >
              <div 
                className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: safeLineColors.flameOrange }}
              />
            </div>
            <p 
              className="text-lg font-semibold"
              style={{ color: safeLineColors.darkCharcoal }}
            >
              Redirecting to login...
            </p>
            <p 
              className="text-sm mt-2"
              style={{ color: safeLineColors.mediumCharcoal }}
            >
              Please wait while we redirect you to the login page
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
